import SectionCallToAction from 'components/sections/sectionAppBanner/SectionCallToAction';
import { useClearParamsAndNavigate } from 'hooks/useClearParamsAndNavigate';
import { useFormattedTitle } from 'hooks/useFormattedTitle';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAnalyticsService } from 'services/analyticsService';
import { getGeoService } from 'services/geoService';

import { AppContext } from '../../../context/app';
import { PrintProvider } from '../../../context/print';
import { KioskType } from '../../../context/types';
import { useCampusComparison } from '../../../hooks/useCampusComparison';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useKioskParams } from '../../../hooks/useKioskParams';
import { useNavigationManager } from '../../../hooks/useNavigationManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import {
  isGozioPlace,
  isLatLng,
  isSatellitePlace,
  isSearchAddressResult,
} from '../../../utils/typeGuards';
import SectionNavigationForm from '../../sections/sectionNavigationForm/SectionNavigationForm';
import AzureMapView from '../../shared/azureMapView/AzureMapView';
import ButtonStyled from '../../shared/button/Button';
import { KioskDirectionsHeader } from '../../shared/kioskDirectionsHeader/KioskDirectionsHeader';
import { LegTwoVisualSteps } from '../../shared/LegTwoVisualSteps/LegTwoVisualSteps';
import ModalPrintDirections from '../../shared/modalPrintDirections/ModalPrintDirections';
import { NavPlaceholder } from '../../shared/navPlaceHolder/NavPlaceHolder';
import SectionNavigationSteps from '../../shared/SectionNavigationSteps/SectionNavigationSteps';
import { ShareBar } from '../../shared/shareBar/ShareBar';
import { Tutorial } from '../../tutorial/TutorialModal';
import styles from './campusNavigationTemplate.module.scss';
import { MobileNavigationTemplate } from './MobileNavigationTemplate';

let nonReactiveStartingPlace: any;
let nonReactiveDestinationPlace: any;

const CampusNavigationTemplate: React.FC = () => {
  const {
    directions,
    setDirections,
    navStartingPlace,
    navDestinationPlace,
    showPrintDirectionView,
    legToggle,
    setLegToggle,
    setMapLoading,
  } = useContext(AppContext);
  const geoService = useMemo(() => getGeoService(), []);
  const analyticsService = useMemo(() => getAnalyticsService(), []);

  nonReactiveStartingPlace = navStartingPlace;
  nonReactiveDestinationPlace = navDestinationPlace;

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const clearParamsAndNavigate = useClearParamsAndNavigate();

  useEffect(() => {
    if (params.get('fromQRcode') === 'true') {
      analyticsService.track(analyticsService.events.QRCodeUsed, {
        destinationId: nonReactiveDestinationPlace?.id,
        kioskId: params.get('kioskId') || undefined,
        startingAddress: nonReactiveStartingPlace?.address,
        startingId: nonReactiveStartingPlace?.id,
      });
      clearParamsAndNavigate(['fromQRcode']);
    }
    // This is intentionally disabled to prevent an infinite loop if we put clearParamsAndNavigate in the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsService, params]);

  const { legTwoRoute, arrivalGeoNode, geoNodeIsLoading } =
    useNavigationManager();
  const { siteName } = useNetworkManager();
  const { kiosk, kioskMode } = useKioskManager();

  const { t } = useTranslation('common');
  const [showNavigationRoute, setShowNavigationRoute] =
    useState<boolean>(false);
  const navLocationsExist = useMemo(
    () => !!navStartingPlace && !!navDestinationPlace,
    [navStartingPlace, navDestinationPlace],
  );

  const navigationRoutesAvailable = useMemo(
    () =>
      !!directions ||
      (legTwoRoute?.navSteps?.length !== undefined &&
        legTwoRoute?.navSteps?.length > 0),
    [directions, legTwoRoute?.navSteps?.length],
  );
  const isMobile = useIsMobile();
  const { isSameCampus } = useCampusComparison();
  const navigate = useNavigate();
  const { id: kioskId, model: kioskModel } = useKioskParams() || {};

  const driveTime = useMemo(
    () => Math.round(directions?.travelTime.inSeconds / 60) || null,
    [directions],
  );

  const address = useMemo(
    () =>
      navStartingPlace && 'address' in navStartingPlace
        ? navStartingPlace.address
        : '',
    [navStartingPlace],
  );

  const handleNavStepsClose = () => {
    setShowNavigationRoute(false);
    setLegToggle(false);

    if (kioskMode) {
      navigate(`/campus-directory?kioskId=${kioskId}&kioskModel=${kioskModel}`);
    }
  };

  const handleShowNavSteps = () => {
    analyticsService.track(analyticsService.events.ViewDirections, {
      destinationId: navDestinationPlace?.id,
      driveTime,
      // eslint-disable-next-line no-extra-parens
      ...(kioskMode && {
        kioskId,
      }),
      // eslint-disable-next-line no-extra-parens
      ...(!kioskMode && {
        address,
      }),
    });
    setShowNavigationRoute(true);
  };

  const toggleMapLoad = useCallback(
    (isLoading: boolean) => {
      setMapLoading(isLoading);
    },
    [setMapLoading],
  );

  // Fetch route
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const isSatelliteDestination = isSatellitePlace(navDestinationPlace);
    const isSatelliteOrigin = isSatellitePlace(navStartingPlace);
    if (
      !(navStartingPlace && arrivalGeoNode) &&
      !(navStartingPlace && isSatelliteDestination) &&
      !(navDestinationPlace && isSatelliteOrigin)
    ) {
      return;
    }

    const getOrigin = () => {
      if (isSearchAddressResult(navStartingPlace)) {
        return {
          lat: navStartingPlace.geolocation.lat,
          lng: navStartingPlace.geolocation.lon,
        };
      } else if (isLatLng(navStartingPlace)) {
        return navStartingPlace;
      } else if (isSatelliteOrigin) {
        return {
          lat: navStartingPlace.destination_geo.lat,
          lng: navStartingPlace.destination_geo.lon,
        };
      }
    };

    const getDestination = () => {
      if (isSatellitePlace(navDestinationPlace)) {
        return {
          lat: navDestinationPlace.destination_geo.lat,
          lng: navDestinationPlace.destination_geo.lon,
          id: navDestinationPlace.id,
        };
      }
      if (arrivalGeoNode) {
        return {
          lat: arrivalGeoNode.lat,
          lng: arrivalGeoNode.lon,
          id: navDestinationPlace?.id,
        };
      }
    };

    const originRequest = getOrigin();
    const destinationRequest = getDestination();
    if (!originRequest || !destinationRequest) return;
    toggleMapLoad(true);
    geoService
      .getRouteInstructions(
        [originRequest.lat, originRequest.lng],
        [destinationRequest.lat, destinationRequest.lng],
        navDestinationPlace?.id,
      )
      .then((result) => {
        setDirections(result);
        toggleMapLoad(false);
      });
  }, [
    geoService,
    setDirections,
    navStartingPlace,
    arrivalGeoNode,
    setMapLoading,
    navDestinationPlace,
    toggleMapLoad,
  ]);

  const isCTS = useMemo(
    () => kiosk?.model === KioskType.CTS_DIGITAL_SIGNAGE,
    [kiosk?.model],
  );

  const siteTitle = useFormattedTitle(
    'campusNavigation.placeholder.title',
    siteName || '',
  );

  return showPrintDirectionView ? (
    <PrintProvider>
      <ModalPrintDirections />
    </PrintProvider>
  ) : isMobile ? (
    <MobileNavigationTemplate handleNavStepsClose={handleNavStepsClose} />
  ) : (
    <div
      className={`${
        kioskMode && kiosk ? styles[kiosk.model] : styles.navLayout
      }`}
    >
      {!kioskMode && <Tutorial />}
      {/*For CTS, we don't need Nav Controls. Display the CTS banner */}
      {isCTS && (
        <>
          <SectionCallToAction origin="CampusNavigation" />
          <KioskDirectionsHeader
            destination={navDestinationPlace?.name || ''}
            onClose={() => handleNavStepsClose()}
          />
        </>
      )}

      {/*NavControls for  Webapp, and mobile, AllMighty, BA. Display a list of steps that user can toggle through*/}
      {!isCTS && (
        <div className={styles.navControlsContainer}>
          {/*Form state 1: starting and destination not set. Not showing nav steps. show the input forms. */}
          {!showNavigationRoute && !kioskMode && <SectionNavigationForm />}
          {/*Form state 2: Navigation steps created. */}
          {navigationRoutesAvailable && (
            <>
              {/*Form state 2.1 show navigation steps (aka skip the "view directions" button)*/}
              {showNavigationRoute || kioskMode ? (
                <SectionNavigationSteps onClose={() => handleNavStepsClose()} />
              ) : (
                <>
                  {/*Form state 2.2 Show share bar and 'view directions' button*/}
                  {!showNavigationRoute && navLocationsExist && (
                    <>
                      <ShareBar showDriveTime={!isSameCampus} topSeparator />
                      <ButtonStyled
                        onClick={() => handleShowNavSteps()}
                        uppercase
                      >
                        {t('campusNavigation.form.viewDirections')}
                      </ButtonStyled>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/*Nav Maps Driving & walking for WebApp, AllMighty, BA, CTS.*/}
      <section className={styles.navMapsContainer}>
        {/*Condition 1: start and dest not set && Condition 2: start not set. dest set && Condition 3: start is on campus*/}
        {((!navStartingPlace && !navDestinationPlace) ||
          (!navStartingPlace && navDestinationPlace) ||
          (navStartingPlace &&
            !navDestinationPlace &&
            isGozioPlace(navStartingPlace))) && (
          <NavPlaceholder
            title={siteTitle}
            subtitle={t('campusNavigation.placeholder.subtitle', {
              siteName,
            })}
          />
        )}
        {/*Condition 4: start set and is a location away from campus. dest not set*/}
        {navStartingPlace &&
          !isGozioPlace(navStartingPlace) &&
          !navDestinationPlace &&
          !kioskMode && <AzureMapView />}
        {/*Condition 5: both are set*/}
        {navStartingPlace && navDestinationPlace && (
          <section className={styles.sectionDirections}>
            {/*if navigating within same campus, don't show map. no need for toggle*/}
            {/*Condition 5.1: Navigating within same campus*/}
            {isSameCampus ? (
              <>
                {geoNodeIsLoading ? (
                  <NavPlaceholder
                    mapLoading={true}
                    title={t('labels.loading')}
                    subtitle={t('labels.delay')}
                  />
                ) : legTwoRoute ? (
                  <div
                    className={
                      kioskMode && kiosk?.model === KioskType.ELO_TOUCH
                        ? styles.mapAndCTA
                        : ''
                    }
                  >
                    <LegTwoVisualSteps onExit={() => handleNavStepsClose()} />
                    {kioskMode && kiosk?.model === KioskType.ELO_TOUCH && (
                      <SectionCallToAction
                        origin="CampusNavigation"
                        orientation="vertical"
                      />
                    )}
                  </div>
                ) : (
                  <NavPlaceholder
                    mapLoading={true}
                    title={t('labels.loading')}
                    subtitle={t('labels.delay')}
                  />
                )}
              </>
            ) : (
              <>
                {/*Condition 5.2: Navigating outside campus*/}
                {geoNodeIsLoading ? (
                  <NavPlaceholder
                    mapLoading={true}
                    title={t('labels.loading')}
                    subtitle={t('labels.delay')}
                  />
                ) : legToggle ? (
                  <LegTwoVisualSteps onExit={() => handleNavStepsClose()} />
                ) : (
                  <AzureMapView />
                )}
              </>
            )}
          </section>
        )}
      </section>
    </div>
  );
};

export default CampusNavigationTemplate;
